import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { buildCsv, downloadCsv } from '../../utils/exportCsv';
import './Salon.css';

const EXPORT_COLUMNS = [
  { key: 'id', labelKey: 'dresses.dressId' },
  { key: 'name', labelKey: 'dresses.dressName' },
  { key: 'brand_name', labelKey: 'dresses.brand' },
  { key: 'rental_price', labelKey: 'dresses.rentalPrice' },
  { key: 'selling_price', labelKey: 'dresses.sellingPrice' },
  { key: 'size', labelKey: 'dresses.size' },
  { key: 'colour', labelKey: 'dresses.color' },
  { key: 'arrival_date', labelKey: 'dresses.arrivalDate' },
  { key: 'stock_count', labelKey: 'dresses.stockCount' },
  { key: 'reservation_count', labelKey: 'dresses.reserveCount' },
  { key: 'reservation_count_current_year', labelKey: 'dresses.reserveCountCurrentYear' },
  { key: 'item_code', labelKey: 'dresses.itemCode' },
  { key: 'country_of_origin', labelKey: 'dresses.countryOfOrigin' },
  { key: 'manufacturer', labelKey: 'dresses.manufacturer' },
  { key: 'importer', labelKey: 'dresses.importer' },
  { key: 'material_composition', labelKey: 'dresses.materialComposition' },
  { key: 'maintenance_procedure', labelKey: 'dresses.maintenanceProcedure' },
  { key: 'shrinkage_during_care', labelKey: 'dresses.shrinkageDuringCare' },
  { key: 'care_washing', labelKey: 'dresses.careWashingField' },
  { key: 'care_bleaching', labelKey: 'dresses.careBleachingField' },
  { key: 'care_drying', labelKey: 'dresses.careDryingField' },
  { key: 'care_ironing', labelKey: 'dresses.careIroningField' },
  { key: 'care_dry_cleaning', labelKey: 'dresses.careDryCleaningField' },
  { key: 'notes', labelKey: 'dresses.notes' },
];

const ExportDressesCsvModal = ({ isOpen, onClose, salonId }) => {
  const { t } = useLanguage();
  const [includeZeroStock, setIncludeZeroStock] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIncludeZeroStock(false);
      setError('');
    }
  }, [isOpen]);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/dresses/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_zero_stock: includeZeroStock ? 'true' : 'false' },
      });

      const dresses = response.data.dresses || [];
      if (dresses.length === 0) {
        setError(t('dresses.exportCsvNoDresses', 'No dresses match the selected export options.'));
        return;
      }

      const headerLabels = EXPORT_COLUMNS.map((col) => t(col.labelKey));
      const rows = dresses.map((dress) => {
        const row = {};
        EXPORT_COLUMNS.forEach((col, index) => {
          row[headerLabels[index]] = dress[col.key];
        });
        return row;
      });

      const csvString = buildCsv(headerLabels, rows);

      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(`dresses-${salonId}-${date}.csv`, csvString);
      onClose();
    } catch (err) {
      console.error('Error exporting dresses:', err);
      setError(
        err.response?.data?.error
        || t('messages.error.failedToExportDresses', 'Failed to export dresses')
      );
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h3>{t('dresses.exportCsvTitle', 'Export dresses to CSV')}</h3>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={includeZeroStock}
                onChange={(e) => setIncludeZeroStock(e.target.checked)}
              />
              <span className="checkmark"></span>
              {t('dresses.exportCsvIncludeZeroStock', 'Include dresses with 0 stock count')}
            </label>
          </div>
          <p className="form-hint" style={{ marginTop: 0 }}>
            {t(
              'dresses.exportCsvIncludeZeroStockHint',
              'By default, dresses with zero stock are excluded from the export.'
            )}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={exporting}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="salon-button primary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting
              ? t('dresses.exportCsvExporting', 'Exporting...')
              : t('dresses.exportCsvDownload', 'Download CSV')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDressesCsvModal;
